import { useEffect, useState } from "react";
import { Trash2, Plus, Save } from "lucide-react";
import toast from "react-hot-toast";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import Drawer from "@/components/Drawer";
import { LoadingState } from "@/components/LoadingState";
import { notificationApi, roleApi } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import { useOrgId } from "@/hooks/useOrgId";
import type { AlertType, NotificationPreference, RolePermissions } from "@/types";

const PERMISSION_LABELS: { key: keyof RolePermissions; label: string }[] = [
  { key: "can_view_dashboard", label: "View dashboard" },
  { key: "can_manage_buildings", label: "Manage buildings" },
  { key: "can_manage_units", label: "Manage units" },
  { key: "can_manage_meters", label: "Manage meters" },
  { key: "can_enter_readings", label: "Enter readings" },
  { key: "can_upload_csv", label: "Upload CSV" },
  { key: "can_view_reports", label: "View reports" },
  { key: "can_export_data", label: "Export data" },
  { key: "can_manage_users", label: "Manage users" },
  { key: "can_create_users", label: "Create users" },
  { key: "can_view_alerts", label: "View alerts" },
  { key: "can_manage_alerts", label: "Manage alerts" },
  { key: "can_view_common_areas", label: "View common areas" },
  { key: "can_manage_common_areas", label: "Manage common areas" },
];

export default function SettingsPage() {
  const user = useAuthStore((s) => s.user)!;
  const orgId = useOrgId();
  const canManage = user.is_staff || !!user.role?.can_manage_users;
  const [tab, setTab] = useState<"roles" | "notifications">("roles");
  const [roles, setRoles] = useState<RolePermissions[]>([]);
  const [prefs, setPrefs] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    const [r, p] = await Promise.all([
      orgId ? roleApi.list(orgId) : Promise.resolve([]),
      notificationApi.list(),
    ]);
    setRoles(r);
    setPrefs(p);
    setLoading(false);
  };
  useEffect(() => {
    loadAll();
  }, [orgId]);

  return (
    <AppLayout>
      <PageHeader title="Settings" subtitle="Roles, permissions, and notification preferences" />
      <div className="card overflow-hidden mb-6">
        <div className="flex border-b border-slate-200">
          <button onClick={() => setTab("roles")} className={`px-5 py-3 text-sm font-medium ${tab === "roles" ? "text-brand-700 border-b-2 border-brand-700" : "text-slate-600"}`} type="button">Roles</button>
          <button onClick={() => setTab("notifications")} className={`px-5 py-3 text-sm font-medium ${tab === "notifications" ? "text-brand-700 border-b-2 border-brand-700" : "text-slate-600"}`} type="button">Notifications</button>
        </div>
        <div className="p-6">
          {loading ? (
            <LoadingState />
          ) : tab === "roles" ? (
            <RolesSection roles={roles} canManage={canManage} orgId={orgId} reload={loadAll} creating={creating} setCreating={setCreating} />
          ) : (
            <NotificationsSection prefs={prefs} reload={loadAll} />
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function RolesSection({
  roles, canManage, orgId, reload, creating, setCreating,
}: {
  roles: RolePermissions[];
  canManage: boolean;
  orgId: string;
  reload: () => Promise<void>;
  creating: boolean;
  setCreating: (b: boolean) => void;
}) {
  const [newName, setNewName] = useState("");
  const [newPerms, setNewPerms] = useState<Record<string, boolean>>({});

  const create = async () => {
    if (!newName) return;
    try {
      await roleApi.create(orgId, { name: newName, ...newPerms } as Partial<RolePermissions>);
      toast.success("Role created");
      setCreating(false);
      setNewName("");
      setNewPerms({});
      reload();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const remove = async (role: RolePermissions) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await roleApi.delete(orgId, role.id);
      toast.success("Role deleted");
      reload();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold">Roles &amp; permissions</h3>
        {canManage && (
          <button onClick={() => setCreating(true)} className="btn-primary" type="button">
            <Plus size={16} /> New role
          </button>
        )}
      </div>
      <div className="space-y-3">
        {roles.map((r) => (
          <div key={r.id} className="card p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="font-medium text-slate-900">{r.name}</span>
                {r.is_system && <span className="badge bg-slate-100 text-slate-600 ml-2">system</span>}
              </div>
              {!r.is_system && canManage && (
                <button onClick={() => remove(r)} className="text-red-600 hover:text-red-700" type="button">
                  <Trash2 size={16} />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-slate-600">
              {PERMISSION_LABELS.filter((p) => r[p.key]).map((p) => (
                <div key={p.key} className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-brand-500 rounded-full" />
                  {p.label}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Drawer
        open={creating}
        onClose={() => setCreating(false)}
        title="Create role"
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" type="button" onClick={() => setCreating(false)}>Cancel</button>
            <button className="btn-primary" type="button" onClick={create}>Create</button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Role name</label>
            <input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} />
          </div>
          <div>
            <label className="label">Permissions</label>
            <div className="space-y-1">
              {PERMISSION_LABELS.map((p) => (
                <label key={p.key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!newPerms[p.key]}
                    onChange={(e) => setNewPerms({ ...newPerms, [p.key]: e.target.checked })}
                  />
                  {p.label}
                </label>
              ))}
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
}

function NotificationsSection({
  prefs, reload,
}: { prefs: NotificationPreference[]; reload: () => Promise<void> }) {
  const [local, setLocal] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const m: Record<string, boolean> = {};
    prefs.forEach((p) => (m[p.alert_type] = p.email_enabled));
    setLocal(m);
  }, [prefs]);

  const save = async () => {
    try {
      const items = (Object.keys(local) as AlertType[]).map((k) => ({ alert_type: k, email_enabled: local[k] }));
      await notificationApi.update(items);
      toast.success("Preferences saved");
      reload();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  return (
    <div>
      <h3 className="text-base font-semibold mb-4">Email notifications</h3>
      <div className="space-y-3">
        {(["anomaly", "missing_reading", "threshold"] as AlertType[]).map((t) => (
          <label key={t} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
            <span className="text-sm font-medium text-slate-700 capitalize">{t.replace("_", " ")}</span>
            <input
              type="checkbox"
              checked={!!local[t]}
              onChange={(e) => setLocal({ ...local, [t]: e.target.checked })}
            />
          </label>
        ))}
      </div>
      <div className="flex justify-end mt-4">
        <button onClick={save} className="btn-primary" type="button">
          <Save size={16} /> Save preferences
        </button>
      </div>
    </div>
  );
}
